import { AxiosResponse } from 'axios'
import { queryOptions } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { NResources } from '../interfaces/services/resources.interfaces'
import { createUrl } from './helpers'
import axiosAuth from './config'
import { IResourceModel } from '../interfaces/models/resources.interfaces'
import i18next from '../i18n/i18n'
import { largeNumberFormattingWithSpaces } from '../helpers/number'

export const getResourcesPerSociety = (
  societyId: string
): Promise<AxiosResponse<NResources.NGetPerSocieties.IResponse>> => {
  const body: NResources.NGetPerSocieties.IRequestBody = {
    societyIds: [societyId],
  }
  return axiosAuth.post<NResources.NGetPerSocieties.IResponse>(
    createUrl('/resources/get-per-societies'),
    body
  )
}

export const getResource = (
  id: string
): Promise<AxiosResponse<NResources.NGetById.IResponse>> => {
  return axiosAuth.get<NResources.NGetById.IResponse>(
    createUrl(`/resources/resource/${id}`)
  )
}

export const patchResource = (params: {
  id: string
  body: NResources.NPatch.IRequestBody
}): Promise<AxiosResponse<NResources.NPatch.IResponse>> => {
  return axiosAuth.patch<NResources.NPatch.IResponse>(
    createUrl(`/resources/resource/${params.id}`),
    params.body
  )
}

export const createResource = (
  body: NResources.NCreate.IRequestBody
): Promise<AxiosResponse<NResources.NCreate.IResponse>> => {
  return axiosAuth.post<NResources.NCreate.IResponse>(
    createUrl(`/resources/resource`),
    body
  )
}

export const deleteResource = (params: {
  id: string
}): Promise<AxiosResponse<NResources.NDel.IResponse>> => {
  return axiosAuth.delete<NResources.NDel.IResponse>(
    createUrl(`/resources/resource/${params.id}`)
  )
}

export const resourceQueries = {
  getResource: (resourceId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'resource', entity: 'detail', resourceId }],
      queryFn: async ({ queryKey }) => {
        return (await getResource(queryKey[0].resourceId!)).data.data
      },
      enabled: !!resourceId,
    }),
  getResourcesPerSociety: (societyId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'resource', entity: 'society', societyId }],
      queryFn: async ({ queryKey }) => {
        return (await getResourcesPerSociety(queryKey[0].societyId!)).data.data
      },
      enabled: !!societyId,
    }),
}

export function getResourceCostString(
  resource?: Pick<IResourceModel, 'cost' | 'isFree'>
): string {
  // If only numbers, format with SEK currency.
  // If not, simply return the cost. Old app did not enforce only number cost string,
  // So we have to take that in consideration as to not break stuff.
  if (!resource) return ''
  if (resource.isFree) {
    return `${i18next.t('resource.free')}`
  }
  if (/^\d+$/.test(resource.cost)) {
    try {
      const cost = Number(resource.cost)
      if (cost > 0) {
        return `${largeNumberFormattingWithSpaces(cost)} kr`
      }
      return `${i18next.t('resource.free')}`
    } catch (error) {
      captureException(error)
      return resource.cost
    }
  }
  return resource.cost
}
